import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BookingSection from '../components/BookingSection';
import QuickBookSection from '../components/QuickBookSection';
import ReviewStrip from '../components/ReviewStrip';
import LocationInfo from '../components/LocationInfo';
import { Toaster } from 'sonner';

/**
 * Dedicated booking page — a focused, distraction-free experience.
 *
 * Rendered at `/book`. Loads the existing BookingSection component (so ALL
 * pricing rules, deals, promos, credits, add-ons keep working exactly as
 * they do on the homepage) but strips away header, footer, hero, gallery,
 * and every other homepage section that competes for attention.
 *
 * Header/banner "Book" buttons point here so casual visitors see only the
 * booking form, not the full marketing page.
 */
const BookNow = () => {
  useEffect(() => {
    document.title = 'Book a Session · High Quality Studios';
    // Reset scroll to top so the user lands on the form, not mid-scroll.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0a0518] to-black text-white">
      <Toaster
        position="top-center"
        richColors
        toastOptions={{
          style: {
            background: '#1a0a2e',
            color: '#fff',
            border: '1px solid rgba(168, 85, 247, 0.3)',
          },
        }}
      />

      {/* Slim, purpose-built top bar — no full nav, just a back link and logo */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-black/70 border-b border-purple-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            to="/"
            data-testid="book-back-to-home"
            className="flex items-center gap-2 text-sm text-purple-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_63dac94a-0f2a-4ccd-9e98-d528f614ae89/artifacts/78xremtt_IMG_3108.jpeg"
              alt="High Quality Studios"
              className="h-9 w-9 rounded-full object-cover border border-purple-500/60 transition-transform group-hover:scale-105"
            />
            <span className="hidden sm:inline text-sm font-semibold tracking-wide text-white">
              HIGH QUALITY STUDIOS
            </span>
          </Link>
        </div>
      </div>

      {/* Simplified page header */}
      <section className="pt-10 pb-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-400/30 mb-4">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-medium text-purple-200">24/7 Booking Available</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Book Your <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-yellow-300 bg-clip-text text-transparent">Session</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-gray-400 max-w-xl mx-auto">
            Pick your engineer, pick a time, and secure your spot. Payment is instant, confirmation is automatic.
          </p>
        </div>
      </section>

      {/* Quick Book — 1-click hot slots for TODAY */}
      <QuickBookSection />

      {/* The actual booking form — reuses the full-featured BookingSection */}
      <div id="booking" className="pb-24">
        <BookingSection hideHeader />
      </div>

      <section className="px-4 sm:px-6 pb-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-white mb-4">What artists say</h2>
          <ReviewStrip />
        </div>
      </section>
      <LocationInfo />

      {/* Slim footer */}
      <footer className="border-t border-purple-900/30 py-6 text-center text-xs text-gray-500">
        <div className="max-w-4xl mx-auto px-4">
          <p>
            High Quality Studios · East Williamsburg, Brooklyn · Montrose Av (L) ·{' '}
            <Link to="/terms" className="text-purple-400 hover:text-purple-300">Terms</Link>
            {' · '}
            <Link to="/privacy" className="text-purple-400 hover:text-purple-300">Privacy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookNow;
